import React from 'react';

/** True while a page shows a lesson version for review: editors keep their layout but offer no edits. */
export const LessonReviewReadOnlyContext = React.createContext(false);

export function useLessonReviewReadOnly(): boolean {
    return React.useContext(LessonReviewReadOnlyContext);
}

/**
 * True while a review shows Your version, which the teacher may edit. Content editors then offer their Edit
 * controls again (still no readiness or AI actions), and their Save keeps the edit in the review draft; the
 * review's Save writes it to the lesson. Answers and sentences or lines can be added, removed and moved through
 * ReviewRowEditContext; whole activities and scripts cannot.
 */
export const LessonReviewEditContext = React.createContext(false);

export function useLessonReviewEdit(): boolean {
    return React.useContext(LessonReviewEditContext);
}

/** The label of an editor's save button: in a review draft it keeps the edit in Your version. */
export function useReviewSaveLabel(label: string): string {
    return React.useContext(LessonReviewEditContext) ? 'Keep in your version' : label;
}

/** An activity row an editor saves: an answer or pair, a sentence or script line, or the script's name. */
export type ReviewRowKind = 'exercise_step_choice' | 'exercise_step_sentence' | 'exercise_step_conversation';

/**
 * Your version under review: an activity row's save (an answer, a sentence or line, a script's name), and adding,
 * removing or moving one, is kept in the review draft through this instead of being written.
 */
export const ReviewRowEditContext = React.createContext<ReviewRowEdits | null>(null);

/** A removal's confirmation in Your version: it changes only the review draft. */
export function useReviewRemoveText(noun: string): string | undefined {
    return React.useContext(ReviewRowEditContext)
        ? `This removes the ${noun} from Your version only. Nothing in the lesson changes until you save your version, and Undo of that save can restore a saved ${noun} while nothing it saved has changed since. Edits you kept to this ${noun} are dropped.`
        : undefined;
}

/** An activity's rows that Your version changes: a row's fields, an added or removed row, and a list's order. */
export interface ReviewRowEdits {
    update: (kind: ReviewRowKind, id: number, fields: Record<string, unknown>) => void;
    create: (kind: 'exercise_step_choice' | 'exercise_step_sentence', parent: ReviewRowParentId, fields: Record<string, unknown>) => void;
    remove: (kind: 'exercise_step_choice' | 'exercise_step_sentence', id: number) => void;
    order: (kind: 'exercise_step_choice' | 'exercise_step_sentence', parent: ReviewRowParentId, ids: number[]) => void;
}
/** The activity, or a conversation's script, that holds a list of rows, by the id shown. */
export interface ReviewRowParentId { kind: 'exercise_step' | 'exercise_step_conversation'; id: number }

/** Receives which editors hold text not yet kept in Your version, so the review asks before redrawing the page. */
export const LessonReviewUnkeptContext = React.createContext<((key: string, unkept: boolean) => void) | null>(null);

/** Reports, while ``unkept``, that this editor holds text not yet kept in Your version. */
export function useReviewUnkept(key: string, unkept: boolean) {
    const mark = React.useContext(LessonReviewUnkeptContext);
    React.useEffect(() => {
        if (!mark) return;
        mark(key, unkept);
        return () => mark(key, false);
    }, [mark, key, unkept]);
}
